import * as THREE from 'three'
import type { MeshPrimitive } from '@/plugins/mesh/types'

interface MeshPickGroup {
  readonly bounds: THREE.Box3
  readonly objects: Set<THREE.Mesh>
}

function isFiniteBox(bounds: THREE.Box3): boolean {
  return (
    !bounds.isEmpty() &&
    [bounds.min.x, bounds.min.y, bounds.min.z, bounds.max.x, bounds.max.y, bounds.max.z].every(
      Number.isFinite
    )
  )
}

function isStaticPrimitive(primitive: MeshPrimitive, root: THREE.Object3D): boolean {
  const object = primitive.object
  if (
    object instanceof THREE.InstancedMesh ||
    object instanceof THREE.SkinnedMesh ||
    object.raycast !== THREE.Mesh.prototype.raycast ||
    Object.keys(object.geometry.morphAttributes).length > 0 ||
    !isFiniteBox(primitive.boundingBox)
  ) {
    return false
  }
  // 资产根可由宿主移动；内部动态层级无法使用加载时固化的 bounds。
  let ancestor: THREE.Object3D | null = object
  while (ancestor && ancestor !== root) {
    if (ancestor.matrixAutoUpdate) return false
    ancestor = ancestor.parent
  }
  return ancestor === root
}

/** 缓存静态 Primitive 的资产局部包围盒，按 GLB 提前筛除无关拾取对象。 */
export class MeshPickIndex {
  private readonly _groups = new Map<string | undefined, MeshPickGroup>()
  private readonly _fallback = new Set<THREE.Mesh>()
  private readonly _objectOrder = new Map<THREE.Mesh, number>()
  private readonly _inverseRoot = new THREE.Matrix4()
  private readonly _localRay = new THREE.Ray()

  constructor(
    private readonly _root: THREE.Object3D,
    primitives: readonly MeshPrimitive[] = []
  ) {
    this.append(primitives)
  }

  /** 在坐标适配完成后追加静态部分，已有组按需扩展包围盒。 */
  append(primitives: readonly MeshPrimitive[]): void {
    for (const primitive of primitives) {
      const object = primitive.object
      if (!this._objectOrder.has(object)) this._objectOrder.set(object, this._objectOrder.size)
      if (!isStaticPrimitive(primitive, this._root)) {
        this._fallback.add(object)
        continue
      }
      let group = this._groups.get(primitive.pickGroupId)
      if (!group) {
        group = { bounds: new THREE.Box3(), objects: new Set() }
        this._groups.set(primitive.pickGroupId, group)
      }
      group.bounds.union(primitive.boundingBox)
      group.objects.add(object)
    }
  }

  /** 完整加载结果交接时丢弃全部预览对象引用。 */
  replace(primitives: readonly MeshPrimitive[]): void {
    this._groups.clear()
    this._fallback.clear()
    this._objectOrder.clear()
    this.append(primitives)
  }

  /** 只返回包围盒命中的可见对象，顺序与原始 Primitive 遍历一致。 */
  query(ray: THREE.Ray): THREE.Mesh[] {
    this._root.updateWorldMatrix(true, false)
    const localDeterminant = this._root.matrix.determinant()
    const determinant = this._root.matrixWorld.determinant()
    const canTransform =
      localDeterminant !== 0 &&
      Number.isFinite(localDeterminant) &&
      determinant !== 0 &&
      Number.isFinite(determinant)
    const objects = new Set(this._fallback)
    if (canTransform) {
      this._inverseRoot.copy(this._root.matrixWorld).invert()
      this._localRay.copy(ray).applyMatrix4(this._inverseRoot)
    }
    for (const group of this._groups.values()) {
      if (canTransform && !this._localRay.intersectsBox(group.bounds)) continue
      for (const object of group.objects) objects.add(object)
    }
    const candidates = [...objects].filter((object) => object.visible)
    candidates.sort((left, right) => this._objectOrder.get(left)! - this._objectOrder.get(right)!)
    for (const object of candidates) object.updateWorldMatrix(true, false)
    return candidates
  }
}
